/**
 * 载荷（payload）的字段级编解码。
 *
 * 这是整个协议栈唯一需要正确的地方 —— 帧头与 CRC 由 `serializer` / `parser` 处理，
 * 而"第 17 个字节是哪个字段的哪一半"全在这里。因此字段布局来自 `descriptor` 的
 * 编译结果（偏移与宽度算一次、缓存住），这个文件只做读写。
 *
 * 值的类型约定（写在一处，避免调用方猜）：
 *
 *   XML 类型                解码得到          编码接受
 *   ─────────────────────  ────────────────  ───────────────────────
 *   char[n]                string            string
 *   uint8_t[n]             Uint8Array        ArrayLike<number> / string(Base64 不支持，用数组)
 *   int8_t[n]              Int8Array         ArrayLike<number>
 *   其它数值类型[n]         number[]          ArrayLike<number>
 *   int64_t / uint64_t     bigint            number | bigint
 *   int64_t[n] / ...       bigint[]          ArrayLike<number | bigint>
 *   其余标量               number            number
 *
 * 64 位整数一律用 `bigint` 表示：`AUTOPILOT_VERSION.uid` 这类字段的实际取值会超过
 * `Number.MAX_SAFE_INTEGER`，用 number 存会静默改变数值 —— 而它恰恰是"这是哪块板子"
 * 的身份标识，改了就认错飞机了。
 */
import { compileMessage, type CompiledField } from './descriptor';
import { MavlinkEncodeError } from './frame';
import { FIELD_TYPE_SIZES, MAVLINK_VERSION, type MessageDefinition } from './types';

/** 解码得到的字段值 */
export type FieldValue =
  | number
  | bigint
  | string
  | Uint8Array
  | Int8Array
  | readonly number[]
  | readonly bigint[];

/** 编码接受的字段值 */
export type FieldInput = number | bigint | string | ArrayLike<number | bigint>;

export type MessageValues = Readonly<Record<string, FieldInput | undefined>>;

/** 解码得到的一条消息：字段名 → 值 */
export type DecodedValues = Readonly<Record<string, FieldValue>>;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder('utf-8', { fatal: false });

/** 定长的 `char[n]` 在 NUL 处结束，之后的内容是填充 */
function readString(payload: Uint8Array, offset: number, length: number): string {
  let end = offset;
  const limit = offset + length;
  while (end < limit && payload[end] !== 0) end += 1;
  return textDecoder.decode(payload.subarray(offset, end));
}

function asNumber(value: FieldInput, context: string): number {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new MavlinkEncodeError(`${context} 不是有限数：${String(value)}`);
    }
    return value;
  }
  if (typeof value === 'bigint') {
    const converted = Number(value);
    if (!Number.isSafeInteger(converted)) {
      throw new MavlinkEncodeError(`${context} 的取值 ${value} 超出双精度整数精度，请按整数写入该字段`);
    }
    return converted;
  }
  throw new MavlinkEncodeError(`${context} 期望数值，实际是 ${typeof value}`);
}

function asBigInt(value: FieldInput, context: string): bigint {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) {
      throw new MavlinkEncodeError(`${context} 的取值 ${value} 无法无损转换为 64 位整数`);
    }
    return BigInt(value);
  }
  throw new MavlinkEncodeError(`${context} 期望 64 位整数，实际是 ${typeof value}`);
}

function writeScalar(view: DataView, field: CompiledField, value: FieldInput, context: string): void {
  const { offset, definition } = field;
  switch (definition.type) {
    case 'uint8_t':
    case 'uint8_t_mavlink_version':
      view.setUint8(offset, asNumber(value, context) & 0xff);
      return;
    case 'char':
      // 标量 char 在 MAVLink 里等同于 int8_t，但调用方更可能给一个单字符字符串
      if (typeof value === 'string') {
        view.setUint8(offset, value.length === 0 ? 0 : (value.codePointAt(0) ?? 0) & 0xff);
        return;
      }
      view.setInt8(offset, asNumber(value, context));
      return;
    case 'int8_t':
      view.setInt8(offset, asNumber(value, context));
      return;
    case 'int16_t':
      view.setInt16(offset, asNumber(value, context), true);
      return;
    case 'uint16_t':
      view.setUint16(offset, asNumber(value, context) & 0xffff, true);
      return;
    case 'int32_t':
      view.setInt32(offset, asNumber(value, context) | 0, true);
      return;
    case 'uint32_t':
      view.setUint32(offset, asNumber(value, context) >>> 0, true);
      return;
    case 'int64_t':
      view.setBigInt64(offset, asBigInt(value, context), true);
      return;
    case 'uint64_t':
      view.setBigUint64(offset, asBigInt(value, context), true);
      return;
    case 'float':
      view.setFloat32(offset, asNumber(value, context), true);
      return;
    case 'double':
      view.setFloat64(offset, asNumber(value, context), true);
      return;
    default:
      throw new MavlinkEncodeError(`${context} 的类型 ${String(definition.type)} 暂不支持`);
  }
}

function writeArray(
  payload: Uint8Array,
  view: DataView,
  field: CompiledField,
  value: FieldInput,
  context: string,
): void {
  const { offset, definition, elementCount, elementSize } = field;

  if (definition.type === 'char') {
    if (typeof value !== 'string') {
      throw new MavlinkEncodeError(`${context} 期望字符串`);
    }
    const encoded = textEncoder.encode(value);
    // 超出定长的部分直接截断：MAVLink 的字符串字段是定长数组，没有"太长"这个状态
    const length = Math.min(encoded.length, elementCount);
    payload.set(encoded.subarray(0, length), offset);
    return;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') {
    throw new MavlinkEncodeError(`${context} 期望数组`);
  }

  const source = value;
  if (source.length > elementCount) {
    throw new MavlinkEncodeError(
      `${context} 的长度 ${source.length} 超过字段容量 ${elementCount}`,
    );
  }

  for (let index = 0; index < source.length; index += 1) {
    const element = source[index];
    if (element === undefined) continue;
    const at = offset + index * elementSize;
    const elementContext = `${context}[${index}]`;
    switch (definition.type) {
      case 'uint8_t':
        payload[at] = Number(element) & 0xff;
        break;
      case 'int8_t':
        view.setInt8(at, Number(element));
        break;
      case 'int16_t':
        view.setInt16(at, Number(element), true);
        break;
      case 'uint16_t':
        view.setUint16(at, Number(element) & 0xffff, true);
        break;
      case 'int32_t':
        view.setInt32(at, Number(element) | 0, true);
        break;
      case 'uint32_t':
        view.setUint32(at, Number(element) >>> 0, true);
        break;
      case 'int64_t':
        view.setBigInt64(at, asBigInt(element, elementContext), true);
        break;
      case 'uint64_t':
        view.setBigUint64(at, asBigInt(element, elementContext), true);
        break;
      case 'float':
        view.setFloat32(at, Number(element), true);
        break;
      case 'double':
        view.setFloat64(at, Number(element), true);
        break;
      default:
        throw new MavlinkEncodeError(`${elementContext} 的类型 ${String(definition.type)} 暂不支持`);
    }
  }
}

/**
 * 把字段值写进一段定长载荷。
 *
 * 缺失字段的处理规则分三种，都是有意的：
 *   - `uint8_t_mavlink_version`：协议说它"由协议填入"，缺省用当前版本号；
 *   - 扩展字段：缺省零。老固件不认识这些字段，新固件读到零即"未提供"，这正是 MAVLink 2 的设计；
 *   - 其余基础字段：**报错**。字段名敲错、少给一个参数都属于代码缺陷，
 *     静默填零会让缺陷以"飞控行为诡异"的形式出现在飞行中。
 */
export function encodePayload(message: MessageDefinition, values: MessageValues): Uint8Array {
  const compiled = compileMessage(message);
  const payload = new Uint8Array(compiled.wireLength);
  const view = new DataView(payload.buffer);
  const known = new Set(compiled.fields.map((field) => field.definition.name));

  for (const name of Object.keys(values)) {
    if (!known.has(name) && values[name] !== undefined) {
      const fieldNames = compiled.fields.map((field) => field.definition.name).join(', ');
      throw new MavlinkEncodeError(
        `${message.name} 没有字段 ${name}。可用字段：${fieldNames}`,
      );
    }
  }

  for (const field of compiled.fields) {
    const { definition } = field;
    const context = `${message.name}.${definition.name}`;
    let value = values[definition.name];

    if (value === undefined) {
      if (definition.type === 'uint8_t_mavlink_version') {
        value = MAVLINK_VERSION;
      } else if (definition.extension) {
        continue; // 保持零
      } else {
        throw new MavlinkEncodeError(`${message.name} 缺少必需字段 ${definition.name}`);
      }
    }

    if (definition.arrayLength > 0) {
      writeArray(payload, view, field, value, context);
    } else {
      writeScalar(view, field, value, context);
    }
  }

  return payload;
}

function readScalar(view: DataView, field: CompiledField): FieldValue {
  const { offset, definition } = field;
  switch (definition.type) {
    case 'uint8_t':
    case 'uint8_t_mavlink_version':
      return view.getUint8(offset);
    case 'char':
      return view.getInt8(offset);
    case 'int8_t':
      return view.getInt8(offset);
    case 'int16_t':
      return view.getInt16(offset, true);
    case 'uint16_t':
      return view.getUint16(offset, true);
    case 'int32_t':
      return view.getInt32(offset, true);
    case 'uint32_t':
      return view.getUint32(offset, true);
    case 'int64_t':
      return view.getBigInt64(offset, true);
    case 'uint64_t':
      return view.getBigUint64(offset, true);
    case 'float':
      return view.getFloat32(offset, true);
    case 'double':
      return view.getFloat64(offset, true);
    default: {
      // 类型表由生成的代码保证完备，走到这里说明生成物与运行时不同步
      const unexpected: never = definition.type;
      throw new MavlinkEncodeError(`未知字段类型 ${String(unexpected)}`);
    }
  }
}

function readArray(payload: Uint8Array, field: CompiledField): FieldValue {
  const { offset, definition, elementCount } = field;

  if (definition.type === 'char') {
    return readString(payload, offset, elementCount);
  }

  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);

  /*
   * 8 位数组返回**视图**而不是拷贝：`FILE_TRANSFER_PROTOCOL` 与 `GPS_RTCM_DATA`
   * 这类消息动辄 200+ 字节，日志下载与 RTCM 注入时每帧都在跑，
   * 每帧一次拷贝就是白白的 GC 压力。
   * 视图指向的是这一帧自己的载荷缓冲（解析器每帧新分配），因此可以安全地持有。
   */
  if (definition.type === 'uint8_t') {
    return new Uint8Array(payload.buffer, payload.byteOffset + offset, elementCount);
  }
  if (definition.type === 'int8_t') {
    return new Int8Array(payload.buffer, payload.byteOffset + offset, elementCount);
  }

  if (definition.type === 'int64_t' || definition.type === 'uint64_t') {
    const values: bigint[] = [];
    for (let index = 0; index < elementCount; index += 1) {
      const at = offset + index * 8;
      values.push(
        definition.type === 'int64_t' ? view.getBigInt64(at, true) : view.getBigUint64(at, true),
      );
    }
    return values;
  }

  const values: number[] = [];
  const elementSize = FIELD_TYPE_SIZES[definition.type];
  for (let index = 0; index < elementCount; index += 1) {
    const at = offset + index * elementSize;
    switch (definition.type) {
      case 'int16_t':
        values.push(view.getInt16(at, true));
        break;
      case 'uint16_t':
        values.push(view.getUint16(at, true));
        break;
      case 'int32_t':
        values.push(view.getInt32(at, true));
        break;
      case 'uint32_t':
        values.push(view.getUint32(at, true));
        break;
      case 'float':
        values.push(view.getFloat32(at, true));
        break;
      case 'double':
        values.push(view.getFloat64(at, true));
        break;
      default:
        throw new MavlinkEncodeError(`未知数组元素类型 ${String(definition.type)}`);
    }
  }
  return values;
}

/**
 * 按字段表读出一帧载荷。
 *
 * 载荷长度必须已补齐到完整长度（解析器保证），否则读数组字段会越界。
 */
export function decodePayload(message: MessageDefinition, payload: Uint8Array): DecodedValues {
  const compiled = compileMessage(message);
  if (payload.length < compiled.wireLength) {
    throw new MavlinkEncodeError(
      `${message.name} 的载荷只有 ${payload.length} 字节，少于完整长度 ${compiled.wireLength}`,
    );
  }

  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  const values: Record<string, FieldValue> = {};
  for (const field of compiled.fields) {
    values[field.definition.name] =
      field.definition.arrayLength > 0 ? readArray(payload, field) : readScalar(view, field);
  }
  return values;
}

/**
 * 便捷读取：把 64 位整数转成 `number`。
 *
 * 只在确定不会丢精度时使用（时间戳、计数）。身份标识类的字段绝不要用它 ——
 * 见文件顶部关于 `uid` 的说明。
 */
export function toNumber(value: FieldValue | undefined): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') {
    const converted = Number(value);
    return Number.isSafeInteger(converted) ? converted : null;
  }
  return null;
}
